import { useCallback, useState } from "react";
import {
  Anchor,
  Building2,
  Factory,
  FileCheck2,
  AlertCircle,
  ArrowDown,
  ArrowUp,
  MapPin,
  PackagePlus,
  Scale,
  Signpost,
  Trash2,
} from "lucide-react";

import { FormField, Input, Select, Textarea } from "../UI/FormField";
import { DirectorySelect } from "../directory/DirectorySelect";
import { AddressAutocompleteField } from "../GooglePlaces/AddressAutocompleteField";
import type { PlacesApiStatus } from "../GooglePlaces/GooglePlacesAutoComplete";
import type { Country, EntitySnapshot } from "../../types";
import { CUSTOM_STOP_KIND_VALUES } from "../../constants/containerOrderConstants";
import type {
  ContainerRouteStop,
  ContainerStopKind,
  ContainerStopRole,
} from "../../types/containerOrders";
import type { StopIssues } from "../../utils/containerLineStops";
import { useTranslation } from "react-i18next";

/**
 * How each part of the route presents, and where its company comes from.
 *
 * The three anchors are picked from a directory — a whole batch is dispatched to
 * them and the record is where their coordinates and contact live. An added stop
 * offers the same picker but does not insist on it: a certified scale on an
 * industrial estate or a customs window is a real place a truck is sent to and
 * is in nobody's address book.
 *
 * `contact` is declared rather than inferred from `source`, because which
 * directory a stop is picked from says nothing about whether there is somebody
 * there to ask for. A terminal gate has no site contact and no loading bay; the
 * client's site and an added stop both do, and the client's site is picked from
 * the same directory as the terminals.
 */
const ROLE_KIND: Record<
  ContainerStopRole,
  {
    source: "addresses" | "partners";
    idField: "addressId" | "partnerId";
    /** Whether this stop has someone to ask for and a bay to report to. */
    contact: boolean;
    icon: typeof Building2;
    accent: string;
  }
> = {
  terminalPickup: {
    source: "addresses",
    idField: "addressId",
    // A terminal gate has neither a ramp nor a person to ask for.
    contact: false,
    icon: Building2,
    accent: "bg-ink",
  },
  loadingSite: {
    // Addresses, like the terminals either side of it: the loading site is a
    // place a truck is sent to. The order's client is a separate field, set on
    // the order rather than here.
    source: "addresses",
    idField: "addressId",
    contact: true,
    icon: Factory,
    accent: "bg-copper",
  },
  terminalDropoff: {
    source: "addresses",
    idField: "addressId",
    contact: false,
    icon: Anchor,
    accent: "bg-ink",
  },
  custom: {
    // Partners rather than addresses: a customs broker or a weighbridge operator
    // is a company one deals with, and that is the directory companies live in.
    source: "partners",
    idField: "partnerId",
    contact: true,
    icon: Signpost,
    accent: "bg-amber-600",
  },
};

/** The icon an added stop wears, by what it is for. */
const KIND_ICON: Record<ContainerStopKind, typeof Building2> = {
  customs: FileCheck2,
  weighing: Scale,
  extraLoading: PackagePlus,
  waypoint: Signpost,
  other: Signpost,
};

interface ContainerLineStopCardProps {
  stop: ContainerRouteStop;
  /** 1-based position on the route, for the card's numbered pip. */
  index: number;
  onChange: (patch: Partial<ContainerRouteStop>) => void;
  disabled: boolean;
  countries: Country[];
  apiKey: string;
  /** Labels for this stop's own slot, which each anchor names differently. */
  refLabel: string;
  dateLabel: string;
  timeLabel: string;
  /** Reported by the route-order rule, per half of the slot. */
  dateError?: string;
  timeError?: string;
  /**
   * What this stop is still missing — already translated. Withheld until a save
   * has been attempted, so a container opened for editing does not greet the
   * user in red.
   */
  issues?: StopIssues;
  /**
   * Route controls. Absent while the container cannot be edited, and each one
   * absent on its own when *this* stop cannot be moved that way — a stop the
   * driver has already been to is settled, and so is everything before it.
   */
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onRemove?: () => void;
  /** Why this stop cannot be rearranged, when it cannot. Shown on the card. */
  lockedReason?: string;
  /**
   * True once either half of this stop has been stamped — by the driver at the
   * gate or by the office from the status field.
   *
   * It closes more than the route controls. Everything that says *where the
   * truck went and when it was due* — the company, the address, the slot, the
   * kind of stop — becomes a matter of record at that moment, and the API
   * refuses to change any of it. So the fields go read-only here rather than
   * staying live over a save that would be rejected: a stamp is only evidence if
   * the thing it is a stamp of cannot be rewritten underneath it, and a form
   * that lets someone type a new address into a visited stop is promising an
   * edit it cannot deliver.
   *
   * What stays open is the operational detail — the gate PIN, the ramp, the
   * contact, the instructions. None of it says where the driver went, all of it
   * is routinely wrong, and correcting it rewrites nothing.
   */
  visited?: boolean;
  /** Rendered under the stop's own fields — the drop-off's cut-off. */
  children?: React.ReactNode;
}

/**
 * One stop of a container's route.
 *
 * The same block whatever part the stop plays — ref, slot, where it is, gate
 * PIN — because a container is dispatched from its own record: everything the
 * driver needs is *on* the container, copied down when it was created, and
 * editable here afterwards. That is what lets one box in a batch be collected a
 * day later, from a different terminal, or by way of a weighbridge the rest of
 * the batch does not visit.
 *
 * Field order matches the order form exactly (ref → date → time → where → PIN):
 * a dispatcher moves between the two all day, and a block that reads differently
 * in the two places is a block they have to re-read. An added stop puts what it
 * is *for* above all of that, because that is the first thing anyone reading an
 * unfamiliar stop needs to know.
 *
 * Two controls sit outside the fields and are about the route rather than the
 * stop: reordering, and the "not used" toggle. The toggle collapses the card to
 * its reason — the address underneath is kept, so turning the stop back on is a
 * click rather than a re-entry, but showing a full form for a place nobody is
 * driving to invites it to be filled in.
 */
export function ContainerLineStopCard({
  stop,
  index,
  onChange,
  disabled,
  countries,
  apiKey,
  refLabel,
  dateLabel,
  timeLabel,
  dateError,
  timeError,
  issues = {},
  onMoveUp,
  onMoveDown,
  onRemove,
  lockedReason,
  visited = false,
  children,
}: ContainerLineStopCardProps) {
  const { t } = useTranslation(["containerOrders", "shipments"]);
  const kindOf = ROLE_KIND[stop.role] ?? ROLE_KIND.custom;
  const custom = stop.role === "custom";
  const Icon = custom ? (KIND_ICON[stop.kind ?? "other"] ?? Signpost) : kindOf.icon;

  const [placesStatus, setPlacesStatus] = useState<PlacesApiStatus>("loading");
  const handlePlacesStatusChange = useCallback(
    (status: PlacesApiStatus) => setPlacesStatus(status),
    [],
  );

  const used = stop.enabled !== false;

  /**
   * The fields that describe the visit itself, closed once it has happened.
   *
   * `disabled` already covers read mode and a locked container; this adds the
   * per-stop rule on top, so one visited stop can be settled while the rest of
   * the route stays fully editable.
   */
  const identityLocked = disabled || visited;

  const coordinates = stop.address?.coordinates ?? undefined;
  const located =
    typeof coordinates?.latitude === "number" &&
    typeof coordinates?.longitude === "number";

  /** What the card is called: the company, the label, or the part it plays. */
  const title = custom
    ? stop.label?.trim() ||
      t(`route.stopKinds.${stop.kind ?? "other"}`)
    : t(`route.${stop.role}`);

  const kindLabel = custom
    ? t(`route.stopKinds.${stop.kind ?? "other"}`)
    : stop.role === "loadingSite"
      ? t("route.kindClient")
      : t("route.kindTerminal");

  const patchAddress = (
    patch: Partial<NonNullable<ContainerRouteStop["address"]>>,
  ) => onChange({ address: { ...(stop.address ?? {}), ...patch } });

  /** A pick from the directory fills the whole block, location included. */
  const applySnapshot = (snapshot: EntitySnapshot) => {
    const country = snapshot.address?.country;
    const snapshotCoordinates = snapshot.address?.coordinates ?? undefined;
    const hasLocation =
      typeof snapshotCoordinates?.latitude === "number" &&
      typeof snapshotCoordinates?.longitude === "number";

    onChange({
      [kindOf.idField]: snapshot._id ?? null,
      companyName: snapshot.companyName ?? "",
      address: {
        street: snapshot.address?.street || "",
        city: snapshot.address?.city || "",
        state: snapshot.address?.state || "",
        postalCode: snapshot.address?.postalCode || "",
        country:
          typeof country === "object" && country !== null
            ? country._id
            : (country as string | undefined),
        coordinates: hasLocation ? snapshotCoordinates : null,
      },
      // Both directories carry a phone and an email; only the stops that have
      // somebody to ask for keep them.
      ...(kindOf.contact
        ? {
            contactPhone: snapshot.phoneNumber || "",
            contactEmail: snapshot.emailAddress || "",
          }
        : {}),
    });
  };

  const clearStop = () =>
    onChange({
      [kindOf.idField]: null,
      companyName: "",
      address: {
        street: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
        coordinates: null,
      },
      ...(kindOf.contact
        ? { contactName: "", contactPhone: "", contactEmail: "" }
        : {}),
    });

  /** What a Google Places pick hands back — see AddressAutocompleteField. */
  const applyPlace = (address: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    latitude: number | null;
    longitude: number | null;
  }) => {
    patchAddress({
      street: address.street,
      ...(address.city ? { city: address.city } : {}),
      ...(address.state ? { state: address.state } : {}),
      ...(address.postalCode ? { postalCode: address.postalCode } : {}),
      ...(address.country ? { country: address.country } : {}),
      coordinates:
        address.latitude != null && address.longitude != null
          ? { latitude: address.latitude, longitude: address.longitude }
          : null,
    });
  };

  // Typing makes the stored location stale — it no longer describes the text in
  // the box — so it goes, exactly as on the order form.
  const handleAddressTyped = (value: string) =>
    patchAddress({ street: value, coordinates: null });

  const placesUnavailable = !apiKey || placesStatus === "error";
  const selectedId = (stop[kindOf.idField] ?? null) as string | null;

  /**
   * Whether this stop can be turned off at all.
   *
   * The loading site cannot: it is the work the order exists for, and the API
   * refuses it too. A stop the driver has already been to cannot either — those
   * two stamps are evidence, and removing the steps under them would break the
   * run's completed prefix.
   */
  const canToggle = stop.role !== "loadingSite" && !visited;

  return (
    <div
      className={`@container rounded-md border bg-white p-3 transition-colors ${
        used
          ? "border-[rgba(11,15,14,0.08)]"
          : "border-dashed border-[rgba(11,15,14,0.18)] bg-bone/60"
      }`}
    >
      <div className="mb-2 flex items-center gap-2">
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${
            used ? kindOf.accent : "bg-secondary-300"
          }`}
        >
          {index}
        </span>
        <Icon
          className={`h-3.5 w-3.5 shrink-0 ${used ? "text-mist" : "text-secondary-300"}`}
        />
        <span
          className={`truncate text-[12px] font-semibold ${
            used ? "text-ink" : "text-mist line-through"
          }`}
        >
          {title}
        </span>
        <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-mist">
          {kindLabel}
        </span>
      </div>

      {/* ── Route controls ───────────────────────────────────────────────────
          About the stop's place in the run rather than about the stop, so they
          sit above the fields and outside them, on their own tinted bar. The
          bar is the point: these were bare hairline icons indistinguishable
          from the status glyphs beside them, and a control that does not look
          like one does not get used. They now have a filled ground, a real
          target size and a hover state that lifts — the ordinary vocabulary for
          "this does something".

          Each one disappears rather than greying out when this particular stop
          cannot be moved that way: a dead arrow on a stop the truck has already
          visited is a control that looks broken, and the reason is stated on the
          card instead. */}
      {!disabled && (onMoveUp || onMoveDown || onRemove || canToggle) && (
        <div className="mb-2 flex flex-wrap items-center gap-1.5 rounded-md bg-bone px-2 py-1.5">
          {(onMoveUp || onMoveDown) && (
            <span className="mr-0.5 text-[10px] font-semibold uppercase tracking-wide text-mist">
              {t("route.moveLabel")}
            </span>
          )}
          {onMoveUp && (
            <button
              type="button"
              onClick={onMoveUp}
              title={t("route.moveUp")}
              aria-label={t("route.moveUp")}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[rgba(11,15,14,0.14)] bg-white text-ink shadow-sm transition-colors hover:border-copper hover:bg-copper hover:text-white focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-copper"
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
          )}
          {onMoveDown && (
            <button
              type="button"
              onClick={onMoveDown}
              title={t("route.moveDown")}
              aria-label={t("route.moveDown")}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[rgba(11,15,14,0.14)] bg-white text-ink shadow-sm transition-colors hover:border-copper hover:bg-copper hover:text-white focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-copper"
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
          )}

          {/* ── "No pickup" ───────────────────────────────────────────────
              A switch rather than a checkbox because it is not a property of
              the stop being recorded, it is the stop being turned off — and the
              two read differently at a glance on a row of eight cards. It says
              what the state *is*, not what clicking would do. */}
          {canToggle && (
            <button
              type="button"
              role="switch"
              aria-checked={!used}
              onClick={() =>
                onChange(
                  used
                    ? { enabled: false }
                    : // Turning it back on takes the reason with it: a stop that
                      // went on carrying "box already on site" while a truck was
                      // being sent to it would show that sentence to the driver.
                      { enabled: true, skipReason: "" },
                )
              }
              className={`ml-auto inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold shadow-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-copper ${
                used
                  ? "border border-[rgba(11,15,14,0.14)] bg-white text-ink hover:border-copper hover:text-copper"
                  : "bg-copper text-white hover:bg-copper/90"
              }`}
            >
              <span
                className={`flex h-3 w-3 rounded-full transition-colors ${
                  used ? "bg-secondary-300" : "bg-white"
                }`}
              />
              {t(used ? "route.markNotUsed" : "route.notUsed")}
            </button>
          )}

          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              title={t("route.removeStop")}
              aria-label={t("route.removeStop")}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[rgba(11,15,14,0.14)] bg-white text-mist shadow-sm transition-colors hover:border-signal hover:bg-signal hover:text-white focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-signal"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Why this stop cannot be rearranged. Stated once, on the card it applies
          to, rather than left to be inferred from missing buttons. */}
      {!disabled && lockedReason && (
        <p className="mb-2 flex items-start gap-1 text-[11px] leading-snug text-mist">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          {lockedReason}
        </p>
      )}

      {/* ── A stop that is not being made ────────────────────────────────────
          Collapsed to the one thing that matters about it: why. The address is
          still stored underneath — turning the stop back on restores the whole
          block — but a form for a place nobody is driving to invites someone to
          fill it in, and the reason is what actually reaches the driver. */}
      {!used ? (
        <FormField
          layout="inline"
          size="sm"
          required
          label={t("route.skipReason")}
          error={issues.skipReason}
          hint={t("route.skipReasonHint")}
        >
          <Textarea
            size="sm"
            rows={2}
            value={stop.skipReason ?? ""}
            onChange={(e) => onChange({ skipReason: e.target.value })}
            disabled={disabled}
            error={!!issues.skipReason}
            placeholder={t("route.skipReasonPlaceholder")}
          />
        </FormField>
      ) : (
        <div className="space-y-1">
          {/* ── What an added stop is for ──────────────────────────────────
              Above everything else, because it is the first thing anyone
              reading an unfamiliar stop needs: a customs office and a rest area
              are the same four address fields and mean entirely different
              things to the driver who has to plan around them. */}
          {custom && (
            <>
              <FormField layout="inline" size="sm" label={t("route.stopKind")}>
                <Select
                  size="sm"
                  value={stop.kind ?? "other"}
                  onChange={(e) =>
                    onChange({ kind: e.target.value as ContainerStopKind })
                  }
                  disabled={identityLocked}
                  options={CUSTOM_STOP_KIND_VALUES.map((kind) => ({
                    value: kind,
                    label: t(`route.stopKinds.${kind}`),
                  }))}
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                label={t("route.stopLabel")}
                hint={t("route.stopLabelHint")}
              >
                <Input
                  size="sm"
                  value={stop.label ?? ""}
                  onChange={(e) => onChange({ label: e.target.value })}
                  disabled={identityLocked}
                  placeholder={t("route.stopLabelPlaceholder")}
                />
              </FormField>
            </>
          )}

          <FormField layout="inline" size="sm" label={refLabel}>
            <Input
              size="sm"
              value={stop.bookingRef ?? ""}
              onChange={(e) =>
                onChange({ bookingRef: e.target.value.toUpperCase() })
              }
              disabled={disabled}
              className="font-mono placeholder:font-sans"
              placeholder={t("placeholders.stopRef")}
            />
          </FormField>

          <FormField layout="inline" size="sm" label={dateLabel} error={dateError}>
            <Input
              size="sm"
              type="date"
              value={stop.date ?? ""}
              onChange={(e) => onChange({ date: e.target.value })}
              disabled={identityLocked}
              error={!!dateError}
            />
          </FormField>

          <FormField layout="inline" size="sm" label={timeLabel} error={timeError}>
            <Input
              size="sm"
              type="time"
              value={stop.time ?? ""}
              onChange={(e) => onChange({ time: e.target.value })}
              disabled={identityLocked}
              error={!!timeError}
            />
          </FormField>

          {/* ── Who is there ───────────────────────────────────────────────
              Two different fields wearing the same label, because the rule
              behind them genuinely differs.

              An anchor is picked from a directory and never typed:
              `DirectorySelect` throws typed text away on blur precisely so a
              batch cannot be dispatched to a company that has no record behind
              it. An added stop is the case that rule was never about — a
              certified scale on an industrial estate, a customs window, a
              lay-by the tachograph forced. None of those is in anyone's address
              book, and refusing to let one be named would make the feature
              unusable for three of its four stated purposes. So it is a plain
              box, and what makes the stop dispatchable is the address
              underneath it, which is held to the same map-location rule as
              every other stop on the route. */}
          {custom ? (
            <FormField
              layout="inline"
              size="sm"
              required
              error={issues.company}
              label={t("route.stopName")}
              hint={t("route.stopNameHint")}
            >
              <Input
                size="sm"
                value={stop.companyName ?? ""}
                onChange={(e) =>
                  onChange({ companyName: e.target.value.toUpperCase() })
                }
                disabled={identityLocked}
                error={!!issues.company}
                placeholder={t("placeholders.stopName")}
              />
            </FormField>
          ) : (
            <FormField
              layout="inline"
              size="sm"
              required
              error={issues.company}
              label={
                stop.role === "loadingSite" || kindOf.source === "partners"
                  ? t("shipments:labels.company")
                  : t("route.terminal")
              }
            >
              <DirectorySelect
                source={kindOf.source}
                selectedId={selectedId}
                value={stop.companyName || ""}
                onSelect={applySnapshot}
                onClear={clearStop}
                disabled={identityLocked}
                error={!!issues.company}
                size="sm"
                placeholder={
                  stop.role === "loadingSite"
                    ? t("placeholders.loadingSite")
                    : kindOf.source === "partners"
                      ? t("placeholders.stopCompany")
                      : t("placeholders.terminal")
                }
              />
            </FormField>
          )}

          <FormField
            layout="inline"
            size="sm"
            required
            label={t("route.address")}
            // Street and city sit in one row under one label, so their two
            // complaints are shown as one line rather than one each.
            error={issues.street ?? issues.city}
          >
            <div className="space-y-1">
              <div className="grid grid-cols-1 gap-2 @xs/control:grid-cols-3">
                <div className="@xs/control:col-span-2">
                  <AddressAutocompleteField
                    apiKey={apiKey}
                    value={stop.address?.street || ""}
                    onChange={handleAddressTyped}
                    onAddressSelect={applyPlace}
                    onStatusChange={handlePlacesStatusChange}
                    countries={countries}
                    disabled={identityLocked}
                    error={!!issues.street}
                    size="sm"
                  />
                </div>
                <Input
                  size="sm"
                  value={stop.address?.city || ""}
                  onChange={(e) => patchAddress({ city: e.target.value })}
                  disabled={identityLocked}
                  error={!!issues.city}
                  placeholder={t("placeholders.city")}
                />
              </div>

              {/* Said at every step rather than only on save — the same
                  statement the order form makes, because it is the same
                  requirement. Once a save has been refused for it, the sentence
                  becomes the rule's own: "pick it from the suggestions", which
                  is the only way to get a location at all. */}
              {!identityLocked &&
                (located ? (
                  <p className="flex items-center gap-1 text-[11px] text-success-600">
                    <MapPin className="h-3 w-3 shrink-0" />
                    {t("route.locationCaptured", {
                      lat: coordinates!.latitude!.toFixed(5),
                      lng: coordinates!.longitude!.toFixed(5),
                    })}
                  </p>
                ) : (
                  <p className="flex items-center gap-1 text-[11px] text-signal">
                    <AlertCircle className="h-3 w-3 shrink-0" />
                    {issues.location ??
                      t(
                        placesUnavailable
                          ? "route.locationLookupUnavailable"
                          : "route.locationNotCaptured",
                      )}
                  </p>
                ))}
            </div>
          </FormField>

          {/* The client's site and the added stops both have someone to ask for
              and somewhere to report to; a terminal gate has neither. */}
          {kindOf.contact && (
            <>
              <FormField layout="inline" size="sm" label={t("route.ramp")}>
                <Input
                  size="sm"
                  value={stop.ramp ?? ""}
                  onChange={(e) => onChange({ ramp: e.target.value })}
                  disabled={disabled}
                  placeholder={t("placeholders.ramp")}
                />
              </FormField>

              <FormField layout="inline" size="sm" label={t("route.contact")}>
                <div className="grid grid-cols-1 gap-2 @2xs/control:grid-cols-2">
                  <Input
                    size="sm"
                    value={stop.contactName ?? ""}
                    onChange={(e) => onChange({ contactName: e.target.value })}
                    disabled={disabled}
                    placeholder={t("placeholders.contactName")}
                  />
                  <Input
                    size="sm"
                    value={stop.contactPhone ?? ""}
                    onChange={(e) => onChange({ contactPhone: e.target.value })}
                    disabled={disabled}
                    placeholder={t("placeholders.contactPhone")}
                    error={!!issues.contactPhone}
                  />
                </div>
              </FormField>
            </>
          )}

          <FormField layout="inline" size="sm" label={t("route.pin")}>
            <Input
              size="sm"
              value={stop.pin ?? ""}
              onChange={(e) => onChange({ pin: e.target.value })}
              disabled={disabled}
              className="font-mono placeholder:font-sans"
              placeholder={t("placeholders.pin")}
            />
          </FormField>

          {/* ── What to do here ────────────────────────────────────────────
              Offered on every stop, not only added ones: "window 3, ask for
              Marek" is as useful at a terminal gate as at a customs office, and
              it is the only field on the card that speaks to the driver in
              sentences. It is shown on their screen at this stop. */}
          <FormField
            layout="inline"
            size="sm"
            label={t("route.stopComment")}
            hint={t("route.stopCommentHint")}
          >
            <Textarea
              size="sm"
              rows={2}
              value={stop.comment ?? ""}
              onChange={(e) => onChange({ comment: e.target.value })}
              disabled={disabled}
              placeholder={t("route.stopCommentPlaceholder")}
            />
            {/* Who wrote what is in the box, when there is a record of it.
                Shown to the dispatcher for the same reason it is shown to the
                driver: a note three weeks old about a terminal whose hours have
                changed reads exactly like one written this morning, and this is
                the only thing that tells them apart. Stamped by the server when
                the text changes — see `recordRouteChanges` — so editing the
                note here moves the byline, and moving the stop does not. */}
            {stop.commentAt && (
              <p className="mt-1 text-[11px] text-[#8A9590]">
                {t("route.stopCommentBy", {
                  who:
                    `${stop.commentBy?.firstName ?? ""} ${
                      stop.commentBy?.lastName ?? ""
                    }`.trim() || t("route.stopCommentUnknownAuthor"),
                  when: new Date(stop.commentAt).toLocaleString(),
                })}
              </p>
            )}
          </FormField>

          {children}
        </div>
      )}
    </div>
  );
}
